import { capitalize } from 'lodash';
import { toLiteralType } from '@util/types';

export enum Season {
    SPRING = 'spring',
    SUMMER = 'summer',
    FALL = 'fall',
    WINTER = 'winter',
}

export type SeasonAndYear = {
    season: Season;
    year: number;
};

export const toSeason = (str: string) => toLiteralType(str, 'season', Object.values(Season));

const winterSeasonDisplay = (season: Season, year: number, fullYear: boolean) => {
    const secondYear = fullYear ? `${year + 1}` : `${(year + 1).toString().slice(2)}`;
    return `${season} ${year}-${secondYear}`;
};

const nonWinterSeasonDisplay = (season: Season, year: number) => `${season} ${year}`;

export const buildDisplaySeason = (season: Season, year: number, fullYear = false): string => {
    return capitalize(
        season === Season.WINTER ? winterSeasonDisplay(season, year, fullYear) : nonWinterSeasonDisplay(season, year),
    );
};

const convertMonthNumberToSeason = (month: number): Season => {
    switch (month) {
        case 0:
        case 1:
        case 2:
            return Season.WINTER;
        case 3:
        case 4:
        case 5:
            return Season.SPRING;
        case 6:
        case 7:
        case 8:
            return Season.SUMMER;
        case 9:
        case 10:
        case 11:
            return Season.FALL;
        default:
            throw new Error('Passed in a month integer that does not represent an actual month');
    }
};

export const getCurrentSeason = (date: Date = new Date()): SeasonAndYear => {
    const season = convertMonthNumberToSeason(date.getMonth());
    const year = date.getFullYear();
    return {
        season,
        year: season === Season.WINTER ? year - 1 : year,
    };
};

export const getNextSeason = ({ season, year: currYear }: SeasonAndYear): SeasonAndYear => {
    let year = currYear;
    let nextSeason: Season;

    switch (season) {
        case Season.WINTER:
            nextSeason = Season.SPRING;
            year += 1;
            break;
        case Season.SPRING:
            nextSeason = Season.SUMMER;
            break;
        case Season.SUMMER:
            nextSeason = Season.FALL;
            break;
        case Season.FALL:
            nextSeason = Season.WINTER;
            break;
        default:
            throw new Error('Unrecognized Season');
    }

    return {
        season: nextSeason,
        year: year,
    };
};

export const getPreviousSeason = (season: SeasonAndYear): SeasonAndYear => {
    switch (season.season) {
        case Season.SPRING:
            return { season: Season.WINTER, year: season.year - 1 };

        case Season.SUMMER:
            return { season: Season.SPRING, year: season.year };

        case Season.FALL:
            return { season: Season.SUMMER, year: season.year };

        case Season.WINTER:
            return { season: Season.FALL, year: season.year };
    }
};

export const getSeasons = (
    numberOfUpcomingSeasons: number = 0,
    numberOfPreviousSeasons: number = 0,
): SeasonAndYear[] => {
    const currentSeason = getCurrentSeason();

    // Get upcoming seasons
    const nextSeasons: SeasonAndYear[] = [];
    let nextSeason = currentSeason;
    for (let i = 0; i < numberOfUpcomingSeasons; i++) {
        nextSeason = getNextSeason(nextSeason);
        nextSeasons.push(nextSeason);
    }

    // Get previous seasons
    const previousSeasons: SeasonAndYear[] = [];
    let previousSeason = currentSeason;
    for (let i = 0; i < numberOfPreviousSeasons; i++) {
        previousSeason = getPreviousSeason(previousSeason);
        previousSeasons.push(previousSeason);
    }

    return [
        // Reverse so that the next upcoming season is last
        ...nextSeasons.reverse(),
        currentSeason,
        ...previousSeasons,
    ];
};

export const isCurrentOrFutureSeason = (seasonName: string, seasonYear: number): boolean => {
    const currentSeason = getCurrentSeason();
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    // Include all teams from current year and beyond
    if (seasonYear > currentYear) {
        return true;
    }

    // For current year, include current season and all seasons that come after
    if (seasonYear === currentYear) {
        const teamSeasonLowercase = seasonName.toLowerCase();
        const currentSeasonLowercase = currentSeason.season.toLowerCase();

        // If it's the current season
        if (teamSeasonLowercase.includes(currentSeasonLowercase)) {
            return true;
        }

        // Winter season spans two years, so check if we're in winter of the previous year
        if (teamSeasonLowercase.includes('winter') && currentSeasonLowercase === 'winter') {
            return true;
        }

        // Special case: If team is Winter and current season is Fall/Summer/Spring, winter is future
        if (teamSeasonLowercase.includes('winter')) {
            return true;
        }

        // Check if the team's season comes after the current season
        const seasonOrder = ['winter', 'spring', 'summer', 'fall'];
        const currentSeasonIndex = seasonOrder.indexOf(currentSeasonLowercase);
        const teamSeasonIndex = seasonOrder.findIndex((s) => teamSeasonLowercase.includes(s));

        if (teamSeasonIndex !== -1 && teamSeasonIndex > currentSeasonIndex) {
            return true;
        }
    }

    // Handle winter season edge case (winter 2024 spans into 2025)
    if (seasonYear === currentYear - 1 && seasonName.toLowerCase().includes('winter')) {
        const currentMonth = currentDate.getMonth();
        // Winter includes Jan (0), Feb (1), March (2)
        if (currentMonth <= 2) {
            return true;
        }
    }

    return false;
};
